<template>
  <div class="min-h-screen bg-[#f9f9f7]">
    <div class="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 space-y-6">
      <!-- Header + company filter -->
      <div class="bg-white shadow-sm rounded-xl p-5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 class="text-2xl font-bold text-[#00225E]">Owner Dashboard</h1>
          <p class="text-sm text-gray-500 mt-1">
            {{ selectedCompany === 'ALL' ? 'All companies' : selectedCompany }}
            <span v-if="scope"> · {{ scope.summary.invoices.count.toLocaleString() }} invoices</span>
          </p>
        </div>
        <div class="flex items-center gap-3">
          <label for="company" class="text-sm font-medium text-gray-700">Company</label>
          <select
            id="company"
            v-model="selectedCompany"
            @change="loadData"
            class="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00225E] min-w-[180px]"
          >
            <option value="ALL">All companies</option>
            <option v-for="c in companies" :key="c" :value="c">{{ c }}</option>
          </select>
          <button
            @click="loadData"
            class="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>
      </div>

      <!-- First load spinner -->
      <div v-if="loading && !scope" class="flex justify-center items-center py-24">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00225E]"></div>
        <span class="ml-2 text-gray-600">Loading dashboard...</span>
      </div>

      <!-- Error -->
      <div v-else-if="error && !scope" class="bg-blue-50 border border-blue-200 rounded-xl p-5">
        <h3 class="text-sm font-medium text-blue-800">Error loading dashboard</h3>
        <p class="mt-2 text-sm text-blue-700">{{ error }}</p>
        <button @click="loadData" class="mt-4 bg-blue-100 px-3 py-2 rounded-md text-sm font-medium text-blue-800 hover:bg-blue-200">
          Try again
        </button>
      </div>

      <div v-else-if="scope" :class="['space-y-6 transition-opacity', loading ? 'opacity-50 pointer-events-none' : '']">
        <!-- Stat tiles -->
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div class="bg-white shadow-sm rounded-xl p-4">
            <p class="text-xs font-medium text-gray-500 flex items-center gap-1">
              Total Invoiced
              <InfoTip title="Total Invoiced" text="Sum of Invoice Amount and the number of invoices in the current scope (all companies, or the selected one). Rejected and In Review invoices are excluded from the total; invoices with no status are shown as In Review." />
            </p>
            <p class="text-2xl font-bold text-[#0b0b0b] mt-1 tabular-nums">{{ money(scope.summary.invoices.amount) }}</p>
            <p class="text-xs text-gray-400 mt-1">{{ scope.summary.invoices.count.toLocaleString() }} invoices</p>
          </div>
          <div class="bg-white shadow-sm rounded-xl p-4">
            <p class="text-xs font-medium text-gray-500 flex items-center gap-1">
              Amount Paid
              <InfoTip title="Amount Paid" text="Amount and count of invoices whose external status is 'Paid'." />
            </p>
            <p class="text-2xl font-bold text-[#006300] mt-1 tabular-nums">{{ money(scope.summary.paid.amount) }}</p>
            <p class="text-xs text-gray-400 mt-1">{{ scope.summary.paid.count.toLocaleString() }} invoices</p>
          </div>
          <div class="bg-white shadow-sm rounded-xl p-4">
            <p class="text-xs font-medium text-gray-500 flex items-center gap-1">
              <svg class="w-3.5 h-3.5 text-[#d03b3b]" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
              At Risk (past due)
              <InfoTip title="At Risk (past due)" text="Total amount and count of open (not-yet-paid) invoices that are past their due date — the sum of the aging buckets 1–30 through 90+." />
            </p>
            <p class="text-2xl font-bold text-[#d03b3b] mt-1 tabular-nums">{{ money(scope.aging.atRisk.amount) }}</p>
            <p class="text-xs text-gray-400 mt-1">{{ scope.aging.atRisk.count.toLocaleString() }} invoices</p>
          </div>
          <div class="bg-white shadow-sm rounded-xl p-4">
            <p class="text-xs font-medium text-gray-500 flex items-center gap-1">
              Avg. Days to Pay
              <InfoTip title="Avg. Days to Pay" text="Average of (payment date − invoice date) across paid invoices that have both dates. Shows — when no dated payments exist." />
            </p>
            <p class="text-2xl font-bold text-[#0b0b0b] mt-1 tabular-nums">
              {{ scope.payment.avgDaysToPay != null ? Math.round(scope.payment.avgDaysToPay) : '—' }}
            </p>
            <p class="text-xs text-gray-400 mt-1">{{ scope.payment.paidWithDates.toLocaleString() }} dated payments</p>
          </div>
          <div class="bg-white shadow-sm rounded-xl p-4">
            <p class="text-xs font-medium text-gray-500 flex items-center gap-1">
              On-time Payment
              <InfoTip title="On-time Payment" text="Share of dated payments made on or before the due date (payment date ≤ due date)." />
            </p>
            <p class="text-2xl font-bold text-[#0b0b0b] mt-1 tabular-nums">
              {{ scope.payment.onTimeRate != null ? Math.round(scope.payment.onTimeRate * 100) + '%' : '—' }}
            </p>
            <p class="text-xs text-gray-400 mt-1">paid on or before due date</p>
          </div>
        </div>

        <!-- Row: status breakdown + aging -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div class="bg-white shadow-sm rounded-xl p-5">
            <h3 class="text-base font-semibold text-[#00225E] mb-4 flex items-center gap-1.5">
              Amount by status
              <InfoTip title="Amount by status" text="Invoiced amount grouped into the five external-status buckets: Paid, Approved, In Acceptance, Reject/Dispute, Return. Hover a bar for its amount and invoice count." />
            </h3>
            <HBarChart :items="statusItems" :format="money" aria-label="Amount by status" />
          </div>
          <div class="bg-white shadow-sm rounded-xl p-5">
            <h3 class="text-base font-semibold text-[#00225E] mb-1 flex items-center gap-1.5">
              Open invoices aging
              <InfoTip title="Open invoices aging" text="Unpaid invoices bucketed by days past due (Not due / 1–30 / 31–60 / 61–90 / 90+). Days come from the sheet's Days Past Due, or today − due date when empty. Darker = more overdue. Feeds the 'At Risk' tile." />
            </h3>
            <p class="text-xs text-gray-400 mb-3">Unpaid invoices bucketed by days past due</p>
            <HBarChart :items="agingItems" :format="money" :gutter="110" aria-label="Aging of open invoices" />
          </div>
        </div>

        <!-- Row: monthly trend -->
        <div class="bg-white shadow-sm rounded-xl p-5">
          <h3 class="text-base font-semibold text-[#00225E] mb-1 flex items-center gap-1.5">
            Monthly paid amount
            <InfoTip title="Monthly paid amount" text="Paid amount per calendar month (last 12 months with payments), grouped by payment date — falling back to invoice date when the paid date is missing. Hover for the crosshair value." />
          </h3>
          <p class="text-xs text-gray-400 mb-3">Last {{ scope.trend.length }} months with payments</p>
          <TrendLine :points="trendPoints" :format="money" aria-label="Monthly paid amount" />
        </div>

        <!-- Row: vendors + systems -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div class="bg-white shadow-sm rounded-xl p-5">
            <h3 class="text-base font-semibold text-[#00225E] mb-1 flex items-center gap-1.5">
              Top vendors by paid amount
              <InfoTip title="Top vendors by paid amount" text="The 10 subcontractors with the highest paid amount, grouped by Subcontractor Name. Hover for each vendor's total invoice count." />
            </h3>
            <p class="text-xs text-gray-400 mb-3">Highest paid subcontractors</p>
            <HBarChart :items="vendorItems" :format="money" :gutter="170" aria-label="Top vendors by paid amount" />
          </div>
          <div class="bg-white shadow-sm rounded-xl p-5">
            <h3 class="text-base font-semibold text-[#00225E] mb-1 flex items-center gap-1.5">
              Invoiced amount by system
              <InfoTip title="Invoiced amount by system" text="Total invoiced amount (any status) grouped by the System field, top 10. Blank values are grouped as '(No system)'." />
            </h3>
            <p class="text-xs text-gray-400 mb-3">Total invoiced grouped by system</p>
            <HBarChart :items="systemItems" :format="money" :gutter="150" aria-label="Invoiced amount by system" />
          </div>
        </div>

        <!-- Row: rejection + return reasons -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div class="bg-white shadow-sm rounded-xl p-5">
            <h3 class="text-base font-semibold text-[#00225E] mb-1 flex items-center gap-1.5">
              Top reject/dispute reasons
              <InfoTip title="Top reject/dispute reasons" text="The 8 most common Reason for Rejection values among invoices with status Reject/Dispute, ranked by invoice count. Hover for the associated amount." />
            </h3>
            <p class="text-xs text-gray-400 mb-3">By number of invoices</p>
            <HBarChart :items="rejectionItems" :format="intFmt" :gutter="200" aria-label="Top rejection reasons" />
          </div>
          <div class="bg-white shadow-sm rounded-xl p-5">
            <h3 class="text-base font-semibold text-[#00225E] mb-1 flex items-center gap-1.5">
              Top return reasons
              <InfoTip title="Top return reasons" text="The 8 most common Reason for Return values among invoices with status Return, ranked by invoice count. Hover for the associated amount." />
            </h3>
            <p class="text-xs text-gray-400 mb-3">By number of invoices</p>
            <HBarChart :items="returnItems" :format="intFmt" :gutter="200" aria-label="Top return reasons" />
          </div>
        </div>

        <!-- Breakdown by company (only when viewing all companies) -->
        <div v-if="selectedCompany === 'ALL'" class="bg-white shadow-sm rounded-xl p-5">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 class="text-base font-semibold text-[#00225E] flex items-center gap-1.5">
              Breakdown by company
              <InfoTip title="Breakdown by company" text="One row per company across all companies (unaffected by the filter). Switch between Table, Distribution and Cards views, and sort by amount, invoice count or name. Click a company to focus the whole dashboard on it." />
            </h3>
            <div class="inline-flex self-start rounded-lg border border-gray-200 bg-gray-50 p-0.5">
              <button
                v-for="v in views"
                :key="v.key"
                @click="breakdownView = v.key"
                :class="['px-3 py-1.5 text-sm font-medium rounded-md transition', breakdownView === v.key ? 'bg-white text-[#00225E] shadow-sm' : 'text-gray-500 hover:text-gray-700']"
              >{{ v.label }}</button>
            </div>
          </div>

          <!-- Shared sort control for Distribution & Cards (Table sorts via headers) -->
          <div v-if="breakdownView !== 'table'" class="flex flex-wrap items-center gap-2 mb-4">
            <span class="uppercase tracking-wide text-xs text-gray-400">Sort</span>
            <button
              v-for="b in sortButtons"
              :key="b.key"
              @click="setSort(b.key)"
              :class="['px-3 py-1 rounded-md text-sm font-medium border transition', sort.key === b.key ? 'bg-[#00225E] text-white border-[#00225E]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50']"
            >{{ b.label }}{{ arrow(b.key) }}</button>
          </div>

          <!-- ============ Table view (1b) ============ -->
          <div v-if="breakdownView === 'table'">
          <div class="overflow-x-auto border border-gray-200 rounded-lg">
            <table class="min-w-full text-sm">
              <thead class="bg-gray-50">
                <tr class="border-b border-gray-200">
                  <th
                    @click="setSort('name')"
                    class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:text-[#00225E] whitespace-nowrap"
                  >
                    Company<span class="text-[#00225E]">{{ arrow('name') }}</span>
                  </th>
                  <th
                    v-for="col in columns"
                    :key="col.key"
                    @click="setSort(col.key)"
                    class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:text-[#00225E] whitespace-nowrap"
                  >
                    {{ col.label }}<span class="text-[#00225E]">{{ arrow(col.key) }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in displayRows"
                  :key="row.company"
                  class="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td class="px-4 py-2.5">
                    <button
                      type="button"
                      @click="selectCompany(row.company)"
                      class="font-medium text-[#00225E] hover:underline"
                    >{{ row.company }}</button>
                  </td>
                  <td v-for="(c, i) in row.cells" :key="i" class="px-4 py-2.5 text-right">
                    <div class="flex flex-col items-end leading-tight">
                      <span class="tabular-nums font-semibold" :class="c.zero ? 'text-gray-300' : 'text-[#0b0b0b]'">{{ c.primary }}</span>
                      <span class="tabular-nums text-[10px]" :class="c.zero ? 'text-gray-200' : 'text-gray-400'">{{ c.secondary }}</span>
                    </div>
                  </td>
                </tr>
                <tr v-if="displayRows.length === 0">
                  <td :colspan="columns.length + 1" class="px-4 py-6 text-center text-gray-500">No data available</td>
                </tr>
              </tbody>
              <tfoot v-if="displayRows.length" class="border-t-2 border-gray-300 bg-gray-50">
                <tr>
                  <td class="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[#00225E]">Total</td>
                  <td v-for="(c, i) in totalCells" :key="i" class="px-4 py-3 text-right">
                    <div class="flex flex-col items-end leading-tight">
                      <span class="tabular-nums font-semibold text-[#0b0b0b]">{{ c.primary }}</span>
                      <span class="tabular-nums text-[10px] text-gray-400">{{ c.secondary }}</span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
            <p class="text-xs text-gray-400 mt-2">Tip: click a company name to focus the dashboard on it, or a column header to sort.</p>
          </div>

          <!-- ============ Distribution view (1a) ============ -->
          <div v-else-if="breakdownView === 'distribution'">
            <div class="flex flex-wrap gap-x-4 gap-y-2 mb-4">
              <span v-for="st in STATUSES" :key="st.key" class="inline-flex items-center gap-1.5 text-xs text-gray-500">
                <span class="w-2.5 h-2.5 inline-block" :style="{ background: st.color }"></span>{{ st.label }}
              </span>
            </div>
            <div class="overflow-x-auto">
              <div style="min-width:520px">
                <div
                  v-for="row in distRows"
                  :key="row.name"
                  class="grid gap-4 items-center py-2.5 border-b border-gray-100"
                  style="grid-template-columns:180px 140px 1fr"
                >
                  <button type="button" @click="selectCompany(row.name)" class="text-left text-sm font-medium text-[#00225E] hover:underline truncate">{{ row.name }}</button>
                  <div class="text-right">
                    <div class="text-lg font-semibold tabular-nums text-[#0b0b0b]">{{ row.totalStr }}</div>
                    <div class="text-xs text-gray-400">{{ row.invLabel }}</div>
                  </div>
                  <div class="flex flex-col gap-1">
                    <div class="h-3 flex overflow-hidden border border-gray-200 bg-gray-100">
                      <div v-for="(seg, i) in row.segments" :key="i" :title="seg.title" :style="{ width: seg.width, background: seg.color }"></div>
                    </div>
                    <span class="text-[10px] text-gray-400">{{ row.activityLabel }}</span>
                  </div>
                </div>
                <div class="grid gap-4 items-center pt-3 mt-0.5 border-t-2 border-gray-300" style="grid-template-columns:180px 140px 1fr">
                  <span class="text-sm font-semibold uppercase tracking-wide text-[#00225E]">Total</span>
                  <div class="text-right">
                    <div class="text-lg font-semibold tabular-nums text-[#0b0b0b]">{{ grand.amtStr }}</div>
                    <div class="text-xs text-gray-400">{{ grand.invLabel }}</div>
                  </div>
                  <span class="text-xs text-gray-400">{{ grand.companiesLabel }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- ============ Cards view (1c) ============ -->
          <div v-else>
            <div class="grid gap-3.5" style="grid-template-columns:repeat(auto-fill,minmax(210px,1fr))">
              <div v-for="row in cardRows" :key="row.name" class="border border-gray-200 rounded-lg p-4 flex flex-col gap-1.5">
                <button type="button" @click="selectCompany(row.name)" class="text-left text-sm font-medium text-[#00225E] hover:underline truncate">{{ row.name }}</button>
                <div class="text-2xl font-bold tabular-nums text-[#0b0b0b]">{{ row.totalStr }}</div>
                <div class="text-xs text-gray-400 -mt-1">{{ row.invLabel }}</div>
                <div class="flex flex-wrap gap-1.5 mt-1">
                  <span
                    v-for="(c, i) in row.chips"
                    :key="i"
                    class="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 border border-gray-200 rounded text-gray-600 whitespace-nowrap"
                  >
                    <span class="w-2 h-2 inline-block" :style="{ background: c.color }"></span>{{ c.label }}
                  </span>
                  <span v-if="row.noChips" class="text-[11px] text-gray-300">No active statuses</span>
                </div>
              </div>
            </div>
            <div class="flex justify-between items-center pt-4 mt-4 border-t border-gray-200 text-xs text-gray-500">
              <span>{{ grand.companiesLabel }} · {{ grand.invLabel }}</span>
              <span class="font-semibold tabular-nums text-[#0b0b0b]">Total {{ grand.amtStr }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import HBarChart from "../components/charts/HBarChart.vue";
import TrendLine from "../components/charts/TrendLine.vue";
import InfoTip from "../components/InfoTip.vue";

// Ordinal blue ramp for aging severity (validated: monotone L, light end clears surface).
const AGING_COLORS = {
  notDue: "#86b6ef",
  d1_30: "#5598e7",
  d31_60: "#2a78d6",
  d61_90: "#1c5cab",
  d90: "#104281",
};

export default {
  components: { HBarChart, TrendLine, InfoTip },
  setup() {
    const store = useStore();

    const scope = ref(null);
    const byCompany = ref([]);
    const companies = ref([]);
    const selectedCompany = ref("ALL");
    const loading = ref(false);
    const error = ref(null);

    const loadData = async () => {
      loading.value = true;
      error.value = null;
      try {
        const filters = selectedCompany.value !== "ALL" ? { company: selectedCompany.value } : {};
        const response = await store.dispatch("getOwnerDashboard", filters);
        if (response.ok && response.data) {
          scope.value = response.data.scope;
          byCompany.value = response.data.byCompany || [];
          companies.value = response.data.companies || [];
          if (selectedCompany.value !== "ALL" && !companies.value.includes(selectedCompany.value)) {
            selectedCompany.value = "ALL";
          }
        } else {
          error.value = response.error || "Failed to load dashboard";
        }
      } catch (err) {
        console.error("Error loading owner dashboard:", err);
        error.value = "Failed to load dashboard from server";
      } finally {
        loading.value = false;
      }
    };

    const selectCompany = (company) => {
      selectedCompany.value = company;
      loadData();
    };

    // ---- Formatters ----
    const money = (v) =>
      "$" + new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(v || 0);
    const intFmt = (v) => new Intl.NumberFormat("en-US").format(v || 0);

    // ---- Breakdown table: sorting, totals, stacked cells ----
    const columns = [
      { key: "invoices", label: "Invoices" },
      { key: "paid", label: "Paid" },
      { key: "approved", label: "Approved" },
      { key: "inAcceptance", label: "In Acceptance" },
      { key: "inReview", label: "In Review" },
      { key: "reject", label: "Reject/Dispute" },
      { key: "return", label: "Return" },
      { key: "approvedWO", label: "Approved WO" },
    ];
    const sort = ref({ key: "invoices", dir: "desc" });

    const setSort = (key) => {
      if (sort.value.key === key) {
        sort.value = { key, dir: sort.value.dir === "desc" ? "asc" : "desc" };
      } else {
        sort.value = { key, dir: key === "name" ? "asc" : "desc" };
      }
    };
    const arrow = (key) =>
      sort.value.key === key ? (sort.value.dir === "desc" ? " ↓" : " ↑") : "";

    // Company sorts by name; Invoices by amount; status columns by count.
    const sortVal = (row, key) => {
      if (key === "name") return row.company.toLowerCase();
      if (key === "invoices") return row.invoices.amount;
      if (key === "invcount") return row.invoices.count;
      return row[key].count;
    };
    const sortedCompanies = computed(() => {
      const { key, dir } = sort.value;
      return [...byCompany.value].sort((a, b) => {
        const va = sortVal(a, key), vb = sortVal(b, key);
        if (va < vb) return dir === "asc" ? -1 : 1;
        if (va > vb) return dir === "asc" ? 1 : -1;
        return a.company.localeCompare(b.company);
      });
    });

    // Invoices column leads with the amount; status columns lead with the count.
    // A fully-empty cell (count and amount both 0) is de-emphasized.
    const cellFor = (row, col) => {
      const b = row[col.key];
      const zero = b.count === 0 && b.amount === 0;
      if (col.key === "invoices") return { primary: money(b.amount), secondary: `${b.count} inv`, zero };
      return { primary: String(b.count), secondary: money(b.amount), zero };
    };
    const displayRows = computed(() =>
      sortedCompanies.value.map((row) => ({
        company: row.company,
        cells: columns.map((col) => cellFor(row, col)),
      }))
    );

    const totals = computed(() => {
      const t = {};
      columns.forEach((col) => (t[col.key] = { count: 0, amount: 0 }));
      byCompany.value.forEach((row) => {
        columns.forEach((col) => {
          t[col.key].count += row[col.key].count;
          t[col.key].amount += row[col.key].amount;
        });
      });
      return t;
    });
    const totalCells = computed(() =>
      columns.map((col) => {
        const b = totals.value[col.key];
        if (col.key === "invoices") return { primary: money(b.amount), secondary: `${b.count} inv` };
        return { primary: String(b.count), secondary: money(b.amount) };
      })
    );

    // ---- Breakdown: view switcher (Table / Distribution / Cards) ----
    // Status colors: a single steel-blue ramp + one neutral (from the imported
    // design). Identity is carried by the legend and per-segment tooltips.
    const STATUSES = [
      { key: "paid", label: "Paid", color: "#2c455d" },
      { key: "approved", label: "Approved", color: "#416180" },
      { key: "inAcceptance", label: "In Acceptance", color: "#597ea3" },
      { key: "inReview", label: "In Review", color: "#8aa0b8" },
      { key: "reject", label: "Reject/Dispute", color: "#749dc4" },
      { key: "return", label: "Return", color: "#94bce3" },
      { key: "approvedWO", label: "Approved WO", color: "#b7b7ba" },
    ];
    const views = [
      { key: "table", label: "Table" },
      { key: "distribution", label: "Distribution" },
      { key: "cards", label: "Cards" },
    ];
    const breakdownView = ref("table");
    const sortButtons = [
      { key: "invoices", label: "Amount" },
      { key: "invcount", label: "# Invoices" },
      { key: "name", label: "Company" },
    ];
    const invLabel = (n) => `${n} ${n === 1 ? "invoice" : "invoices"}`;

    // 1a — distribution bar: total up front, one stacked bar per company.
    const distRows = computed(() =>
      sortedCompanies.value.map((r) => {
        const stTotal = STATUSES.reduce((n, s) => n + r[s.key].count, 0);
        const segments = STATUSES.filter((s) => r[s.key].count > 0).map((s) => ({
          width: ((r[s.key].count / stTotal) * 100).toFixed(2) + "%",
          color: s.color,
          title: `${s.label}: ${r[s.key].count} · ${money(r[s.key].amount)}`,
        }));
        return {
          name: r.company,
          totalStr: money(r.invoices.amount),
          invLabel: invLabel(r.invoices.count),
          segments,
          activityLabel: stTotal === 0 ? "No status activity" : `${stTotal} in status flow`,
        };
      })
    );

    // 1c — cards: amount as hero, only active statuses shown as chips.
    const cardRows = computed(() =>
      sortedCompanies.value.map((r) => {
        const chips = STATUSES.filter((s) => r[s.key].count > 0 || r[s.key].amount > 0).map((s) => {
          const { count, amount } = r[s.key];
          return {
            label: amount > 0 ? `${s.label} ${count} · ${money(amount)}` : `${s.label} ${count}`,
            color: s.color,
          };
        });
        return {
          name: r.company,
          totalStr: money(r.invoices.amount),
          invLabel: invLabel(r.invoices.count),
          chips,
          noChips: chips.length === 0,
        };
      })
    );

    const grand = computed(() => ({
      amtStr: money(totals.value.invoices.amount),
      invLabel: invLabel(totals.value.invoices.count),
      companiesLabel: `${byCompany.value.length} ${byCompany.value.length === 1 ? "company" : "companies"}`,
    }));

    // ---- Chart data mappers ----
    const statusItems = computed(() =>
      (scope.value?.statusBreakdown || []).map((s) => ({
        label: s.label,
        value: s.amount,
        sub: `${s.count} invoices`,
      }))
    );
    const agingItems = computed(() =>
      (scope.value?.aging.buckets || []).map((b) => ({
        label: b.label,
        value: b.amount,
        color: AGING_COLORS[b.key],
        sub: `${b.count} invoices`,
      }))
    );
    const trendPoints = computed(() =>
      (scope.value?.trend || []).map((t) => ({ label: t.label, value: t.amount }))
    );
    const vendorItems = computed(() =>
      (scope.value?.topVendors || []).map((v) => ({
        label: v.label,
        value: v.amount,
        sub: `${v.count} invoices`,
      }))
    );
    const systemItems = computed(() =>
      (scope.value?.bySystem || []).map((s) => ({
        label: s.label,
        value: s.amount,
        sub: `${s.count} invoices`,
      }))
    );
    const rejectionItems = computed(() =>
      (scope.value?.reasons.rejection || []).map((r) => ({
        label: r.label,
        value: r.count,
        sub: money(r.amount),
      }))
    );
    const returnItems = computed(() =>
      (scope.value?.reasons.return || []).map((r) => ({
        label: r.label,
        value: r.count,
        sub: money(r.amount),
      }))
    );

    onMounted(loadData);

    return {
      scope, byCompany, companies, selectedCompany, loading, error,
      loadData, selectCompany, money, intFmt,
      statusItems, agingItems, trendPoints, vendorItems, systemItems, rejectionItems, returnItems,
      columns, sort, setSort, arrow, displayRows, totalCells,
      STATUSES, views, breakdownView, sortButtons, distRows, cardRows, grand,
    };
  },
};
</script>
